// Seasonal theme engine — auto-applies based on current date
// Adds subtle seasonal CSS variables and ambient effects

export function getSeasonalTheme() {
  const now = new Date();
  const month = now.getMonth(); // 0-11
  const day = now.getDate();

  // Halloween: Oct 1-31
  if (month === 9) {
    const intensity = day >= 20 ? 'peak' : 'building'; // Gets stronger after Oct 20
    return {
      id: 'halloween',
      name: 'Halloween',
      intensity,
      // Ambient glow shifts to orange/purple
      glowPrimary: intensity === 'peak' ? 'rgba(255, 140, 50, 0.04)' : 'rgba(255, 140, 50, 0.02)',
      glowSecondary: intensity === 'peak' ? 'rgba(160, 80, 220, 0.03)' : 'rgba(160, 80, 220, 0.015)',
      // Accent overrides
      accentHue: intensity === 'peak' ? '#e8850a' : '#d4993a',
      // Special elements
      borderGlow: intensity === 'peak' ? 'rgba(255, 140, 50, 0.12)' : 'rgba(255, 140, 50, 0.06)',
      // Sidebar subtle accent
      sidebarAccent: intensity === 'peak' ? 'rgba(160, 80, 220, 0.04)' : 'transparent',
      // Loading phrases
      loadingPhrases: [
        'Summoning the spirits of great cinema...',
        'Carving the perfect edit...',
        'Brewing something magical...',
        'The haunted timeline awaits...',
        'No tricks, only treats...',
      ],
      // Emoji for badges/accents
      emoji: '🎃',
      sparkleColor: '#ff8c32',
    };
  }

  // Christmas: Dec 1-31
  if (month === 11) {
    const intensity = day >= 15 ? 'peak' : 'building';
    return {
      id: 'christmas',
      name: 'Holiday Season',
      intensity,
      glowPrimary: intensity === 'peak' ? 'rgba(200, 50, 50, 0.03)' : 'rgba(200, 50, 50, 0.015)',
      glowSecondary: intensity === 'peak' ? 'rgba(50, 180, 80, 0.03)' : 'rgba(50, 180, 80, 0.015)',
      accentHue: '#c6a96f', // Keep gold — it's already festive
      borderGlow: intensity === 'peak' ? 'rgba(200, 50, 50, 0.1)' : 'rgba(200, 50, 50, 0.05)',
      sidebarAccent: intensity === 'peak' ? 'rgba(50, 180, 80, 0.03)' : 'transparent',
      loadingPhrases: [
        'Wrapping your edit with care...',
        'Checking the timeline twice...',
        'Dashing through the footage...',
        'Making spirits bright...',
        'Unwrapping your masterpiece...',
        'Decking the halls of your timeline...',
      ],
      emoji: '✨',
      sparkleColor: '#e8443a',
    };
  }

  // Valentine's: Feb 10-14
  if (month === 1 && day >= 10 && day <= 14) {
    return {
      id: 'valentines',
      name: "Valentine's Day",
      intensity: day === 14 ? 'peak' : 'building',
      glowPrimary: 'rgba(220, 80, 100, 0.025)',
      glowSecondary: 'rgba(220, 120, 160, 0.02)',
      accentHue: '#c6a96f',
      borderGlow: 'rgba(220, 80, 100, 0.08)',
      sidebarAccent: 'transparent',
      loadingPhrases: ['Crafting your love story...', 'Every frame is a heartbeat...'],
      emoji: '💍',
      sparkleColor: '#dc5064',
    };
  }

  // New Year's: Dec 28 - Jan 5
  if ((month === 11 && day >= 28) || (month === 0 && day <= 5)) {
    return {
      id: 'newyear',
      name: 'New Year',
      intensity: 'peak',
      glowPrimary: 'rgba(198, 169, 111, 0.05)',
      glowSecondary: 'rgba(198, 169, 111, 0.03)',
      accentHue: '#d4b97e',
      borderGlow: 'rgba(198, 169, 111, 0.15)',
      sidebarAccent: 'transparent',
      loadingPhrases: ['Starting the year fresh...', 'New year, new masterpieces...'],
      emoji: '🥂',
      sparkleColor: '#d4b97e',
    };
  }

  // Fall vibes: Sep 15 - Oct 31 (warm tones — before halloween kicks in for Oct)
  if ((month === 8 && day >= 15) || month === 9) {
    if (month === 9) return null; // Halloween handles October
    return {
      id: 'fall',
      name: 'Fall Season',
      intensity: 'subtle',
      glowPrimary: 'rgba(200, 140, 60, 0.02)',
      glowSecondary: 'rgba(180, 100, 50, 0.015)',
      accentHue: '#c6a96f',
      borderGlow: 'rgba(200, 140, 60, 0.05)',
      sidebarAccent: 'transparent',
      loadingPhrases: ['Capturing the golden hour...', 'Warm tones loading...'],
      emoji: '🍂',
      sparkleColor: '#c88c3c',
    };
  }

  return null; // No seasonal theme
}

// Apply seasonal CSS as inline styles on a container
export function getSeasonalStyles(theme) {
  if (!theme) return {};
  return {
    '--seasonal-glow-primary': theme.glowPrimary,
    '--seasonal-glow-secondary': theme.glowSecondary,
    '--seasonal-border-glow': theme.borderGlow,
    '--seasonal-sidebar-accent': theme.sidebarAccent,
  };
}
